import { AfterViewInit, Component, computed } from '@angular/core';
import { UrlTree } from '@angular/router';
import { delay } from 'rxjs/operators';
import { noAgg0, sumOver, weightedAverage } from '../../services/aggregation';
import { BasicFilterExpression, CompoundFilterExpression, ExportFilter } from '../../services/data.service';
import { att } from '../../services/measures';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { getOnderwijsresultatenSchooljaarRangeString } from '@cumlaude/shared-utils';
import { CijferA, CijferI, CijfersOverzichtComponent } from '../cijfers-overzicht/cijfers-overzicht.component';
import { createMeasureColumn, DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { DashboardContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { Level, Path } from '../../services/data-tree';
import { DashboardVariant } from '../../services/weergave-opties';
import { Att, AttrPath, Examenstatus } from '@cumlaude/metadata';
import { indicatorOverOpties, SchooljaarIndicatorOverComponent } from '../../schooljaar-indicator-over/schooljaar-indicator-over.component';
import { TooltipElement } from '@cumlaude/shared-components-overlays';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { OnderwijsresultatenUitsluitenFilterComponent } from '../../onderwijsresultaten-uitsluiten-filter/onderwijsresultaten-uitsluiten-filter.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

const permanentTable: AttrPath[] = [['cf_fk_ilt', 'ilt_nm_niveau']];

// aantal CE-cijfers voor het betreffende schooljaar voor de betreffende bekostigingings-BRIN moet >= 30 zijn
const uitsluitenFilter = new BasicFilterExpression(['cf_fk_or_exccl', 'or_q_vakken'], 30, '>=');

const permanentFilters = [
	new BasicFilterExpression(['cf_is_diplomavak'], 1),
	// Combinatiecijfervakken HAVO/VWO niet tonen, beroepsgerichte vakken VMBO wel
	new CompoundFilterExpression(
		[
			new BasicFilterExpression(['cf_is_combinatiecijfervak'], 0),
			new BasicFilterExpression(['cf_fk_ilt', 'ilt_nm_niveau'], ['HAVO', 'VWO'], 'not in'),
		],
		'or'
	),
	new BasicFilterExpression(['cf_is_hoogste_ce'], 1),
	new BasicFilterExpression(['cf_fun_nm_examenstatus_met_prognose'], [Examenstatus.GESLAAGD, Examenstatus.AFGEWEZEN], 'in'),
	new CompoundFilterExpression(
		[new BasicFilterExpression(['cf_fk_vk', 'vk_co_vak'], 1107, '<>'), new BasicFilterExpression(['cf_nm_schooljaar'], '2015/2016', '<')],
		'or'
	),
	new BasicFilterExpression(['cf_nr_cijfer'], null, '<>'),
];

@Component({
	selector: 'app-examencijfers',
	templateUrl: './examencijfers.component.html',
	styleUrls: ['./examencijfers.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		SchooljaarIndicatorOverComponent,
		OnderwijsresultatenUitsluitenFilterComponent,
		BarchartTableComponent,
		FilterBarComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
})
export class ExamencijfersComponent extends CijfersOverzichtComponent implements AfterViewInit {
	table: AttrPath[] = permanentTable;

	override fixedBeforeGroups = 1;

	override flexibleGroupsRemovable = false;

	readonly groups = computed(() => {
		const huidigeGroeperingen = this.groeperingService.huidigeGroeperingen();
		return this.variant() === DashboardVariant.ACTUEEL ? huidigeGroeperingen : [...huidigeGroeperingen, ['cf_nm_schooljaar'] as AttrPath];
	});

	override actueelFilters: FilterName[] = [
		'x_onderwijsresultaten_cf_schooljaar',
		'cf_fk_br_vest.br_co_brin',
		'cf_fk_ilt.ilt_nm_niveau',
		'cf_nm_vak',
		'cf_nm_excijf_uitzondering',
	];

	schooljaar?: string;

	aantalJaren!: number;

	uitsluiten = this.qp.signal('or-uitsluiten');

	readonly permanentExpressions = computed(() => (this.uitsluiten() ? [...permanentFilters, uitsluitenFilter] : permanentFilters));

	override ngAfterViewInit() {
		super.ngAfterViewInit();
		this.subscriptions.push(
			this.filterService
				.observe('x_onderwijsresultaten_cf_schooljaar')
				.pipe(delay(0))
				.subscribe(([schooljaar, aantalJaren]) => {
					this.schooljaar = schooljaar;
					this.aantalJaren = aantalJaren;
				})
		);
	}

	protected override getFixedAfterGroups(): number {
		return this.variant() === DashboardVariant.ACTUEEL ? 0 : 1;
	}

	protected override multiAggregators = [weightedAverage('cijfer', 'cf_m_nr_cijfer')];

	protected override singleAggregators = {
		cf_nr_label_onvoldoendes: sumOver('cf_m_nr_label_onvoldoendes'),
		aantalLeerlingen: noAgg0('cf_m_nr_leerlingen'),
	};

	override createTooltip(_path: Path<CijferA, number[]>, _sizeLabel: string, _size: number): TooltipElement[] {
		return [];
	}

	override createMeasureColumns(context: DashboardContext<CijferI, CijferA, ExamencijfersComponent>): ColumnDef<DataRow<CijferA>>[] {
		return [
			createMeasureColumn<CijferI, CijferA>('CE', att('cijfer'), { context, format: '1.2-2' }),
			this.createLeerlingColumn('Cijfers', att('count_records'), { context }, att('aantalLeerlingen')),
		];
	}

	override enrichTableModel(_context: DashboardContext<CijferI, CijferA, ExamencijfersComponent>, tableModel: TableModel<DataRow<CijferA>>) {
		tableModel.showFooters = true;
	}

	getSchooljarenCaption() {
		return this.schooljaar ? getOnderwijsresultatenSchooljaarRangeString(this.schooljaar, this.aantalJaren, 'exc') : '';
	}

	override getAlternatingGroupLevel(): number {
		return this.variant() === DashboardVariant.ACTUEEL ? 1 : 2;
	}

	override getZoomClickUrl(
		_atts: Att[],
		_groups: string[],
		_path: Level<CijferA, number[]>[],
		_options: { ignoreFirst?: number; singleGroup?: boolean } = {}
	): UrlTree | undefined {
		return undefined;
	}

	override getDisplayOptions(): ExportFilter[] {
		return [
			{ label: 'Indicator over', value: indicatorOverOpties[this.aantalJaren] },
			{ label: 'Meetellende vestigingen', value: this.uitsluiten() ? 'Minimaal 30 cijfers' : 'Alle' },
		];
	}
}
